import { createModel } from '@rematch/core';
import { toast } from 'components/RevxMessage';
import { ShippingSplitPayload } from 'domains/shippingDomain';
import {
  FileItem,
  JobNotesItem,
  JobPoItem,
  JobPoView,
  OrgMembers,
  ProofItem,
  ProofMsg,
  Summary,
} from 'domains/workspaceDetailDomain';
import { JobItem, RFMOffer } from 'domains/workspaceDomain';
import i18n from 'i18n/config';
import { RootModel } from 'models';
import { BaseState, baseReducers } from 'models/baseReducers';
import workspaceService from 'services/jobService';
import workspaceDetailService from 'services/workspaceDetailService';
import {
  UPLOAD_FILE_ADDRESS_PREFIX,
  WORKSPACE_DELAY_TIME,
} from 'utils/constants';
import { sleep } from 'utils/misc';

interface WorkspaceDetailState extends BaseState {
  size: number;
  page: number;
}

export const workspaceDetail = createModel<RootModel>()({
  state: {
    size: 10,
    page: 1,
  } as WorkspaceDetailState,
  reducers: {
    ...baseReducers<WorkspaceDetailState>(),
  },
  effects: (dispatch) => {
    const { workspace } = dispatch;
    return {
      async getSummary(payload: number): Promise<Partial<Summary>> {
        dispatch.app.showLoading();
        try {
          const summary = await workspaceDetailService.getSummary(payload);
          dispatch.app.hideLoading();
          return summary;
        } catch {
          dispatch.app.hideLoading();
          return {};
        }
      },
      async getAllFile(payload: JobItem): Promise<FileItem[]> {
        dispatch.app.showLoading();
        try {
          let fileList = await workspaceDetailService.getAllFile(payload.id);
          fileList.forEach((item) => {
            item.name = item.fileName;
            item.uid = item.id + '';
            item.path = item.fileUrl;
            item.url = item.fileUrl;
            item.thumbUrl = UPLOAD_FILE_ADDRESS_PREFIX + item.fileUrl;
            item.status = 'done';
          });
          fileList = fileList.reverse();
          dispatch.app.hideLoading();
          return fileList;
        } catch {
          dispatch.app.hideLoading();
          return [];
        }
      },
      async saveFile(payload: Partial<FileItem>, state): Promise<FileItem> {
        const fileItem = await workspaceDetailService.saveFile({
          jobId: payload.id,
          skuId: payload.skuId,
          fileUrl: payload.path,
          fileName: payload.fileName,
        });
        if (payload.id) {
          await workspace.getJobItemCount(payload.id);
        }
        fileItem.name = fileItem.fileName;
        fileItem.uid = fileItem.id + '';
        fileItem.path = fileItem.fileUrl;
        fileItem.url = fileItem.fileUrl;
        fileItem.thumbUrl = UPLOAD_FILE_ADDRESS_PREFIX + fileItem.fileUrl;
        fileItem.status = 'done';
        if (payload.skuId) {
          fileItem.skuId = payload.skuId;
        }
        return fileItem;
      },
      async deleteFile(payload: Partial<FileItem>): Promise<string> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.deleteFile(payload);
          if (payload.jobId) {
            await workspace.getJobItemCount(payload.jobId);
          }
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return '';
        }
      },
      async fileSubmit(payload: JobItem): Promise<any> {
        return await workspaceDetailService.fileSubmit(payload);
      },
      async getAllFileProof(payload: ProofItem): Promise<FileItem[]> {
        dispatch.app.showLoading();
        try {
          let proofFileList = await workspaceDetailService.getAllProofFile(
            payload
          );
          proofFileList.forEach((item) => {
            item.name = item.fileName;
            item.uid = item.id + '';
            item.path = item.fileUrl;
            item.url = item.fileUrl;
            item.thumbUrl = UPLOAD_FILE_ADDRESS_PREFIX + item.fileUrl;
            item.status = 'done';
          });
          proofFileList = proofFileList.reverse();
          dispatch.app.hideLoading();
          return proofFileList;
        } catch {
          dispatch.app.hideLoading();
          return [];
        }
      },
      async saveProofFile(payload: Partial<FileItem>): Promise<FileItem> {
        dispatch.app.showLoading();
        const fileItem = await workspaceDetailService.saveProofFile({
          jobProofId: payload.id,
          fileUrl: payload.path,
          fileName: payload.fileName,
        });
        fileItem.name = fileItem.fileName;
        fileItem.uid = fileItem.id + '';
        fileItem.path = fileItem.fileUrl;
        fileItem.url = fileItem.fileUrl;
        fileItem.thumbUrl = UPLOAD_FILE_ADDRESS_PREFIX + fileItem.fileUrl;
        fileItem.status = 'done';
        dispatch.app.hideLoading();
        return fileItem;
      },
      async deleteProofFile(payload: Partial<FileItem>): Promise<string> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.deleteProofFile(payload);
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return '';
        }
      },
      async shippingNumber(proofItem: ProofItem): Promise<any> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.shippingNumber(proofItem);
          toast(i18n.t('toast.supplierProofTrackingCodeEntered'));
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },
      async proofMsg(proofMsg: ProofMsg): Promise<any> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.proofMsg(proofMsg);
          toast(i18n.t('toast.proofFeedbackAttached'));
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },
      async updateProofMsg(proofMsg: ProofMsg): Promise<any> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.updateProofMsg(proofMsg);
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },
      async getProofAll(job: JobItem): Promise<ProofItem[]> {
        dispatch.app.showLoading();
        try {
          const proofList = await workspaceDetailService.getProofAll(job);
          proofList?.forEach((item) => {
            item.proofMsg?.forEach((msg) => {
              msg.creatorFullName = `${msg.creatorFirstName} ${msg.creatorLastName}`;
            });
          });
          dispatch.app.hideLoading();
          return proofList;
        } catch {
          dispatch.app.hideLoading();
          return [];
        }
      },
      async nextProofVersion(payload: ProofItem, state): Promise<any> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.nextProofVersion(payload);
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },
      async supplierApproval(payload: ProofItem, state): Promise<any> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.supplierApproval(payload);
          toast(i18n.t('toast.supplierProofUploaded'));
          await sleep(WORKSPACE_DELAY_TIME);
          dispatch.workspace.reloadJobList();
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },
      async buyerApprove(payload: ProofItem, state): Promise<any> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.buyerApprove(payload);
          await sleep(WORKSPACE_DELAY_TIME);
          dispatch.workspace.reloadJobList();
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },
      async buyerDecline(payload: ProofItem, state): Promise<any> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.buyerDecline(payload);
          await sleep(WORKSPACE_DELAY_TIME);
          dispatch.workspace.reloadJobList();
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },
      async saveNote(payload: JobNotesItem): Promise<any> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.saveNote(payload);
          // await dispatch.workspace.getJobDetail(payload.jobId);
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },
      async getNotes(payload: JobItem, state): Promise<JobNotesItem[]> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.getNotes(payload);
          res.forEach((item) => {
            item.creatorFullName = `${item.creatorFirstName} ${item.creatorLastName}`;
          });
          dispatch.app.hideLoading();
          // const oldItem = state.workspace.jobList.find(
          //   (item) => item.id === payload.id
          // );
          // workspace.updateJobPageItem({
          //   ...oldItem,
          //   notesCount: res.length,
          // });
          return res;
        } catch {
          dispatch.app.hideLoading();
          return [];
        }
      },

      async poAccept(payload: JobItem, state): Promise<any> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.poAccept(payload);

          await sleep(WORKSPACE_DELAY_TIME);
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },

      async poDecline(payload: JobItem, state): Promise<any> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.poDecline(payload);
          toast(i18n.t('toast.declinePO'));
          await sleep(WORKSPACE_DELAY_TIME);
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },

      async poSend(payload: JobPoItem): Promise<any> {
        return workspaceDetailService.poSend(payload);
      },

      async poView(payload: JobItem): Promise<Partial<JobPoView>> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.poView(payload);
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return {};
        }
      },

      async splitShipping(
        payload: Partial<ShippingSplitPayload>
      ): Promise<any> {
        try {
          const res = await workspaceDetailService.splitShipping(payload);
          return res || 'success';
        } catch {
          return 'error';
        }
      },

      async batchShippingUpdate(payload: ShippingSplitPayload[]): Promise<any> {
        try {
          const res = await workspaceDetailService.batchShippingUpdate(payload);
          return res || 'success';
        } catch {
          return 'error';
        }
      },

      async getRfmOffers(jobId: number, state): Promise<RFMOffer[]> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceService.rfmOffersByJobId(jobId);
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return [];
        }
      },

      async orgMembers(payload: number): Promise<OrgMembers[]> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceDetailService.orgMembers(payload);
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return [];
        }
      },
    };
  },
});
